import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  ListItemIcon,
  Paper,
  CircularProgress,
  Divider,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  alpha,
} from "@mui/material";
import {
  AdminPanelSettings,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  DoneAll as BulkIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Undo as UndoIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import YearAccessEditor from "./YearAccessEditor";
import { ROLE_OPTIONS, RoleButtonGroup, RoleChip } from "./RoleSelector";
import { RestrictedChip } from "../../CompanyList";

const CompanyMode = ({
  selectedCompanyId,
  companyPermissions,
  companies,
  loadingCompanyPermissions,
  onSaveCompanyUserRoles,
  getAvailableYears,
  getUserStatus,
  isUserInactive,
}) => {
  const [pendingChanges, setPendingChanges] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [bulkRole, setBulkRole] = useState("");
  const [companySnapshot, setCompanySnapshot] = useState({});
  const [bulkYoa, setBulkYoa] = useState([]);
  const [batchMenuAnchor, setBatchMenuAnchor] = useState(null);
  const [bulkYoaDialogOpen, setBulkYoaDialogOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const companyBucket =
    (selectedCompanyId && companyPermissions?.[selectedCompanyId]) || {};

  const members =
    Array.isArray(companyBucket.userList) && companyBucket.userList.length > 0
      ? companyBucket.userList
      : Object.values(companyBucket.users || {});
  const isInactiveMember = (user) =>
    isUserInactive?.(user?.u_id) || getUserStatus?.(user) === "inactive";
  const activeMembers = members.filter((user) => !isInactiveMember(user));

  const companyPendingChanges =
    (selectedCompanyId && pendingChanges?.[selectedCompanyId]) || {};

  const getBasePermission = (uId) =>
    companyBucket.users?.[uId] || members.find((m) => m.u_id === uId) || {};

  const getEffectiveMemberPermission = (uId) => {
    const base = getBasePermission(uId);
    const pending = companyPendingChanges[uId] || {};
    return {
      role: pending.role ?? base.role ?? "none",
      is_partial_access:
        pending.is_partial_access ?? base.is_partial_access ?? false,
      allowed_yoa: pending.allowed_yoa ?? base.allowed_yoa ?? [],
    };
  };

  const getAvailableYearsForMember = (uId) => {
    const perm = getBasePermission(uId);
    const years = getAvailableYears?.(perm) || [];
    if (years.length > 0) return years;
    return getAvailableYears?.(companyBucket.company) || [];
  };

  const handleRoleSelect = (uId, role) => {
    if (!selectedCompanyId) return;
    if (isUserInactive?.(uId)) return;
    setPendingChanges((prev) => ({
      ...prev,
      [selectedCompanyId]: {
        ...(prev[selectedCompanyId] || {}),
        [uId]: { ...(prev[selectedCompanyId]?.[uId] || {}), role },
      },
    }));
  };

  const handlePartialChange = (uId, value) => {
    if (!selectedCompanyId) return;
    if (isUserInactive?.(uId)) return;
    setPendingChanges((prev) => ({
      ...prev,
      [selectedCompanyId]: {
        ...(prev[selectedCompanyId] || {}),
        [uId]: {
          ...(prev[selectedCompanyId]?.[uId] || {}),
          is_partial_access: value,
        },
      },
    }));
  };

  const handleYoaChange = (uId, allowed_yoa) => {
    if (!selectedCompanyId) return;
    if (isUserInactive?.(uId)) return;
    setPendingChanges((prev) => ({
      ...prev,
      [selectedCompanyId]: {
        ...(prev[selectedCompanyId] || {}),
        [uId]: { ...(prev[selectedCompanyId]?.[uId] || {}), allowed_yoa },
      },
    }));
  };

  const handleToggleYear = (uId, year) => {
    const eff = getEffectiveMemberPermission(uId);
    const current = eff.allowed_yoa || [];
    const next = current.includes(year)
      ? current.filter((y) => y !== year)
      : [...current, year].sort((a, b) => a - b);
    handleYoaChange(uId, next);
  };

  const handleSelectAllYears = (uId, selectAll) => {
    const available = getAvailableYearsForMember(uId);
    handleYoaChange(uId, selectAll ? available : []);
  };

  const hasPendingChanges = Object.keys(companyPendingChanges).length > 0;

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!selectedCompanyId) return;
    const bucket = companyPermissions?.[selectedCompanyId];
    const memberList =
      Array.isArray(bucket?.userList) && bucket.userList?.length > 0
        ? bucket.userList
        : Object.values(bucket?.users || {});
    const snap = {};
    memberList.forEach((m) => {
      const uId = m?.u_id;
      if (!uId) return;
      const base = bucket?.users?.[uId] || m;
      const pending = companyPendingChanges[uId] || {};
      snap[uId] = {
        role: pending.role ?? base.role ?? "none",
        is_partial_access:
          pending.is_partial_access ?? base.is_partial_access ?? false,
        allowed_yoa: pending.allowed_yoa ?? base.allowed_yoa ?? [],
      };
    });
    setCompanySnapshot((prev) => ({ ...prev, [selectedCompanyId]: snap }));
  }, [selectedCompanyId, companyPermissions, companyPendingChanges]);

  const handleResetToSnapshot = () => {
    if (!selectedCompanyId) return;
    setPendingChanges((prev) => ({
      ...prev,
      [selectedCompanyId]: {},
    }));
    setBatchMenuAnchor(null);
  };

  const handleSaveChanges = async () => {
    if (!selectedCompanyId || !hasPendingChanges || !onSaveCompanyUserRoles)
      return;

    setIsSaving(true);

    try {
      const items = Object.keys(companyPendingChanges)
        .filter((uId) => !isUserInactive?.(uId))
        .map((uId) => {
          const eff = getEffectiveMemberPermission(uId);
          return { uId, ...eff };
        })
        .map(({ uId, role, is_partial_access, allowed_yoa }) => {
          const isNone = !role || role === "none";
          return {
            target_u_id: uId,
            role: role || "none",
            is_partial_access: isNone ? true : !!is_partial_access,
            allowed_yoa: isNone
              ? []
              : Array.isArray(allowed_yoa)
                ? allowed_yoa
                : [],
          };
        });

      if (items.length > 0) {
        await onSaveCompanyUserRoles(selectedCompanyId, items);
      }
      setPendingChanges((prev) => ({
        ...prev,
        [selectedCompanyId]: {},
      }));
      setToastMessage("Saved");
      setToastOpen(true);
    } catch (error) {
      console.error("Failed to save changes:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApplyRoleToAll = (role) => {
    const r = role ?? bulkRole;
    if (!r) return;
    setPendingChanges((prev) => {
      const next = { ...(prev[selectedCompanyId] || {}) };
      members.forEach((user) => {
        if (!user?.u_id || isInactiveMember(user)) return;
        next[user.u_id] = { ...next[user.u_id], role: r };
      });
      return { ...prev, [selectedCompanyId]: next };
    });
    setBatchMenuAnchor(null);
    setToastMessage("Role applied to all");
    setToastOpen(true);
  };

  const getBulkAvailableYears = () => {
    const fromCompany = getAvailableYears?.(companyBucket.company) || [];
    if (fromCompany.length > 0) return fromCompany;
    const first = members[0]?.u_id;
    return first ? getAvailableYearsForMember(first) : [];
  };

  const bulkAvailableYears = getBulkAvailableYears();

  const handleBulkToggleYear = (year) => {
    setBulkYoa((prev) =>
      prev.includes(year)
        ? prev.filter((y) => y !== year)
        : [...prev, year].sort((a, b) => a - b)
    );
  };

  const handleBulkApplyYoa = () => {
    if (bulkYoa.length === 0) return;
    setPendingChanges((prev) => {
      const next = { ...(prev[selectedCompanyId] || {}) };
      members.forEach((user) => {
        if (!user?.u_id || isInactiveMember(user)) return;
        next[user.u_id] = {
          ...next[user.u_id],
          allowed_yoa: [...bulkYoa],
          is_partial_access: true,
        };
      });
      return { ...prev, [selectedCompanyId]: next };
    });
    setBulkYoaDialogOpen(false);
    setToastMessage("Years applied to all");
    setToastOpen(true);
  };

  const handleBulkSetFullAccess = () => {
    setPendingChanges((prev) => {
      const next = { ...(prev[selectedCompanyId] || {}) };
      members.forEach((user) => {
        if (!user?.u_id || isInactiveMember(user)) return;
        next[user.u_id] = {
          ...next[user.u_id],
          is_partial_access: false,
          allowed_yoa: [],
        };
      });
      return { ...prev, [selectedCompanyId]: next };
    });
    setToastMessage("Full access set");
    setToastOpen(true);
  };

  return (
    <>
      {!selectedCompanyId ? (
        // Empty State - Company mode
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <AdminPanelSettings sx={{ fontSize: 32, color: "grey.400" }} />
          </Box>
          <Typography variant='h6' color='text.primary' gutterBottom>
            Select a company to assign users
          </Typography>
          <Typography variant='body2'>
            Choose a company from the left panel to quickly assign roles for its
            members.
          </Typography>
        </Box>
      ) : (
        <>
          {hasPendingChanges && (
            <Box
              sx={{
                px: 2,
                py: 0.75,
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                color: "warning.dark",
                typography: "caption",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              {Object.keys(companyPendingChanges).length} item(s) to be saved
            </Box>
          )}
          {/* Toolbar: Company info & actions */}
          <Box
            sx={{
              px: 4,
              py: 2.5,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            {(() => {
              const companyFromIndex = companyBucket.company;
              const company =
                companyFromIndex ||
                (companies || []).find((c) => c?.c_id === selectedCompanyId) ||
                {};
              return (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: "primary.lighter",
                        color: "primary.main",
                        fontWeight: 600,
                        fontSize: "1.2rem",
                      }}
                    >
                      {(company?.name || company?.company_name || "?")
                        .charAt(0)
                        .toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography
                        variant='h6'
                        fontWeight={600}
                        lineHeight={1.2}
                      >
                        {company?.name || company?.company_name || "Company"}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {company?.uen || company?.company_uen}
                        {company.isRestricted && <RestrictedChip />}
                      </Typography>
                    </Box>
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      bgcolor: "background.paper",
                      borderRadius: 1.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      justifyContent: "space-between",
                    }}
                  >
                    {/* Tier 1: Search + Role filter — always visible */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        flex: "1 1 260px",
                        minWidth: 0,
                      }}
                    >
                      <TextField
                        size='small'
                        placeholder='Search members...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                          flex: { xs: "1 1 100%", sm: "1 1 auto" },
                          minWidth: 0,
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <SearchIcon fontSize='small' color='action' />
                            </InputAdornment>
                          ),
                          endAdornment: searchTerm && (
                            <InputAdornment position='end'>
                              <IconButton
                                size='small'
                                onClick={() => setSearchTerm("")}
                                edge='end'
                                sx={{ p: 0.5 }}
                              >
                                <ClearIcon fontSize='small' />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormControl size='small' sx={{ minWidth: 130 }}>
                        <Select
                          value={roleFilter}
                          onChange={(e) => setRoleFilter(e.target.value)}
                          displayEmpty
                          startAdornment={
                            <InputAdornment position='start'>
                              <FilterListIcon
                                fontSize='small'
                                sx={{ mr: 0.5 }}
                              />
                            </InputAdornment>
                          }
                          sx={{ bgcolor: "grey.50" }}
                        >
                          <MenuItem value='all'>All roles</MenuItem>
                          {ROLE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <ListItemIcon
                                sx={{
                                  minWidth: 32,
                                  color: `${opt.color}.main`,
                                }}
                              >
                                <opt.Icon fontSize='small' />
                              </ListItemIcon>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Tier 2 & 3: Batch + More (md+) or single ⋮ (xs) */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flex: "0 0 auto",
                      }}
                    >
                      {/* Three action icons */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Tooltip
                          title={
                            activeMembers.length === 0
                              ? "No active members"
                              : "Apply role to all (choose in menu)"
                          }
                        >
                          <span>
                            <IconButton
                              size='small'
                              disabled={activeMembers.length === 0 || isSaving}
                              onClick={(e) =>
                                setBatchMenuAnchor(e.currentTarget)
                              }
                            >
                              <BulkIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Menu
                          anchorEl={batchMenuAnchor}
                          open={Boolean(batchMenuAnchor)}
                          onClose={() => setBatchMenuAnchor(null)}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          PaperProps={{ sx: { minWidth: 200, mt: 0.5 } }}
                        >
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ px: 1.5, py: 0.5, display: "block" }}
                          >
                            Apply role to all
                          </Typography>
                          {ROLE_OPTIONS.map((opt) => (
                            <MenuItem
                              key={opt.value}
                              onClick={() => handleApplyRoleToAll(opt.value)}
                              disabled={activeMembers.length === 0 || isSaving}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 32,
                                  color: `${opt.color}.main`,
                                }}
                              >
                                <opt.Icon fontSize='small' />
                              </ListItemIcon>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Menu>

                        <Tooltip
                          title={
                            bulkAvailableYears.length === 0
                              ? "No years available"
                              : "Apply tax computation years to all"
                          }
                        >
                          <span>
                            <IconButton
                              size='small'
                              disabled={
                                activeMembers.length === 0 ||
                                bulkAvailableYears.length === 0 ||
                                isSaving
                              }
                              onClick={() => setBulkYoaDialogOpen(true)}
                            >
                              <EventIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip
                          title={
                            activeMembers.length === 0
                              ? "No active members"
                              : "Set all full access"
                          }
                        >
                          <span>
                            <IconButton
                              size='small'
                              disabled={activeMembers.length === 0 || isSaving}
                              onClick={handleBulkSetFullAccess}
                            >
                              <CheckCircleIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>

                      <Divider
                        orientation='vertical'
                        flexItem
                        sx={{ display: { xs: "none", md: "flex" }, mx: 0.5 }}
                      />

                      <Tooltip
                        title={
                          !hasPendingChanges && !isSaving
                            ? "No changes to save"
                            : isSaving
                              ? "Saving..."
                              : ""
                        }
                      >
                        <span>
                          <Button
                            size='small'
                            variant='contained'
                            color='primary'
                            disabled={!hasPendingChanges || isSaving}
                            onClick={handleSaveChanges}
                            startIcon={
                              isSaving ? (
                                <CircularProgress size={16} color='inherit' />
                              ) : (
                                <SaveIcon fontSize='small' />
                              )
                            }
                            sx={{
                              whiteSpace: "nowrap",
                              minWidth: 90,
                              px: 1.5,
                              boxShadow: hasPendingChanges ? 2 : 0,
                            }}
                          >
                            {isSaving ? "Saving..." : "Save"}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title='Reset changes'>
                        <span>
                          <Button
                            size='small'
                            variant='outlined'
                            color='secondary'
                            onClick={handleResetToSnapshot}
                            startIcon={<UndoIcon fontSize='small' />}
                          >
                            Reset
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </Paper>

                  {/* Bulk years Dialog */}
                  <Dialog
                    open={bulkYoaDialogOpen}
                    onClose={() => setBulkYoaDialogOpen(false)}
                    maxWidth='xs'
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}
                  >
                    <DialogTitle>
                      Apply tax computation years to all
                    </DialogTitle>
                    <DialogContent>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ display: "block", mb: 1 }}
                      >
                        Members get partial access with selected tax computation
                        years only.
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          maxHeight: 140,
                          overflowY: "auto",
                          p: 1,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                        }}
                      >
                        {bulkAvailableYears.map((year) => {
                          const isSelected = bulkYoa.includes(year);
                          return (
                            <Chip
                              key={year}
                              label={year}
                              size='small'
                              clickable
                              onClick={() => handleBulkToggleYear(year)}
                              color={isSelected ? "primary" : "default"}
                              variant={isSelected ? "filled" : "outlined"}
                              sx={{ height: 24, fontSize: "0.75rem" }}
                            />
                          );
                        })}
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
                        <Button
                          size='small'
                          onClick={() => setBulkYoa([...bulkAvailableYears])}
                        >
                          Select All
                        </Button>
                        <Button size='small' onClick={() => setBulkYoa([])}>
                          Clear
                        </Button>
                      </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 2, pb: 2 }}>
                      <Button onClick={() => setBulkYoaDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        variant='contained'
                        color='primary'
                        disabled={bulkYoa.length === 0}
                        onClick={handleBulkApplyYoa}
                      >
                        Apply to all ({activeMembers.length})
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <Snackbar
                    open={toastOpen}
                    autoHideDuration={3000}
                    onClose={() => setToastOpen(false)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  >
                    <Alert
                      onClose={() => setToastOpen(false)}
                      severity='success'
                      sx={{ width: "100%" }}
                    >
                      {toastMessage}
                    </Alert>
                  </Snackbar>
                </>
              );
            })()}
          </Box>

          {/* Company mode content */}
          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            {loadingCompanyPermissions ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border: "3px solid",
                    borderColor: "grey.200",
                    borderTopColor: "primary.main",
                    animation: "spin 1s linear infinite",
                  }}
                />
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
              </Box>
            ) : (
              <Box sx={{ maxWidth: 1000, mx: "auto" }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1.5fr",
                    px: 2,
                    py: 1,
                    mb: 1,
                    typography: "caption",
                    color: "text.secondary",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  <Box>Member</Box>
                  <Box>Role</Box>
                  <Box sx={{ textAlign: "right" }}>Scope (Years)</Box>
                </Box>

                <Stack spacing={1}>
                  {members.length === 0 ? (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      <Typography variant='body2'>
                        No members assigned to this company yet.
                      </Typography>
                    </Box>
                  ) : (
                    members.map((user, index) => {
                      const uId = user.u_id;
                      const eff = getEffectiveMemberPermission(uId);
                      const role = eff.role;
                      const hasPartialAccess = eff.is_partial_access;
                      const allowed_yoa = eff.allowed_yoa || [];
                      const availableYears = getAvailableYearsForMember(uId);
                      const selectedYearsCount = allowed_yoa.length;
                      const userInactive = isInactiveMember(user);

                      const name = (user?.user_name || "").toLowerCase();
                      const email = (user?.user_email || "").toLowerCase();
                      const keyword = searchTerm.toLowerCase();

                      if (
                        keyword &&
                        !name.includes(keyword) &&
                        !email.includes(keyword)
                      ) {
                        return null;
                      }

                      if (roleFilter !== "all" && role !== roleFilter) {
                        return null;
                      }

                      return (
                        <Box
                          key={uId || user.email || index}
                          sx={{
                            bgcolor: "background.paper",
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 2,
                            overflow: "hidden",
                            opacity: userInactive ? 0.72 : 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "2fr 1fr 1.5fr",
                              alignItems: "center",
                              p: 2,
                              gap: 2,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant='subtitle2'
                                fontWeight={600}
                                noWrap
                              >
                                {user?.user_name || "Unnamed"}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                noWrap
                              >
                                {user?.user_email}
                              </Typography>
                              {userInactive && (
                                <Tooltip title="This user hasn't joined or activated their account yet. Permissions cannot be changed.">
                                  <Chip
                                    label='Inactive'
                                    size='small'
                                    color='warning'
                                    variant='outlined'
                                    sx={{
                                      ml: 1,
                                      mt: 0.5,
                                      height: 20,
                                      cursor: "default",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              {userInactive ? (
                                <RoleChip role={role} isInactive />
                              ) : (
                                <RoleButtonGroup
                                  value={role}
                                  onChange={(r) => handleRoleSelect(uId, r)}
                                  disabled={isSaving}
                                  roles={["none", "viewer", "editor", "admin"]}
                                />
                              )}
                            </Box>

                            <Box sx={{ minWidth: 0, textAlign: "right" }}>
                              {role === "none" ? (
                                <Typography
                                  variant='caption'
                                  color='text.disabled'
                                >
                                  No Access
                                </Typography>
                              ) : (
                                <YearAccessEditor
                                  hasPartialAccess={hasPartialAccess}
                                  onPartialChange={(value) =>
                                    handlePartialChange(uId, value)
                                  }
                                  allowed_yoa={allowed_yoa}
                                  availableYears={availableYears}
                                  onToggleYear={(year) =>
                                    handleToggleYear(uId, year)
                                  }
                                  onSelectAllYears={(selectAll) =>
                                    handleSelectAllYears(uId, selectAll)
                                  }
                                  disabled={userInactive || isSaving}
                                  fullAccessLabel='Full Access'
                                  align='left'
                                  alignNotPartial='right'
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default CompanyMode;
